import React, { useState, useEffect } from 'react';
import { TOAST_OPTIONS } from '../../constants';

//UTILS
import { useSearchParams, useNavigate } from 'react-router-dom';
import AdminService from '../../services/admin_services';
import TokenService from '../../services/token_services';

//VISUALS
import LOGO_BLACK from '../../assets/images/logo_black.png';

//COMPONENTS
import Menu from '../../components/menu/admin';
import BoardHeader from '../../components/boardheader/admin';
import Button from '../../components/button';
import { ToastContainer, toast } from 'react-toastify';
import { ThreeDots } from 'react-loader-spinner';

//TRANSLATION
import { useTranslation } from 'react-i18next';

const AdminUsers = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // eslint-disable-next-line
    const [searchParams, setSearchParams] = useSearchParams();
    const [accounts, setAccounts] = useState([]);
    const [emails, onChangeEmails] = useState();
    const [amount, onChangeAmount] = useState(0);
    const [displayForm, setDisplayForm] = useState(false);
    const [deactivated, setDeactivated] = useState(false);
    const [pending, setPending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sortField, setSortField] = useState('created_date');
    const [sortDirection, setSortDirection] = useState('desc');

    const loadUsers = async () => {
        const pendingState = searchParams.get("pending") === "true";
        const deactivationState = !pendingState && (searchParams.get("deactivated") === "true");
        setDeactivated(deactivationState);
        setPending(pendingState);
        setAccounts([]);
        const users = await AdminService.getUsers(deactivationState, pendingState);
        setAccounts(users)
    }

    const sendInvits = async () => {
        try {
            setLoading(true)
            const trimmed = emails.replace(/ /g, '').split(',');
            let mailingList = []
            // eslint-disable-next-line
            let re = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/;
            for (let entry of trimmed) {
                if (!entry || entry.length < 4) {
                    console.error('Invalid entry')
                } else if (!re.test(entry)) {
                    toast.warn(`${entry} ${t('admin.invalid_format_invit')}`, TOAST_OPTIONS);
                } else
                    mailingList.push(entry)
            }
            const resp = await AdminService.inviteUsers(mailingList, amount);
            if (resp.status) {
                toast.success(t('admin.invits_sent'), TOAST_OPTIONS);
                loadUsers();
            } else
                toast.error(resp.message, TOAST_OPTIONS);
            setLoading(false)
        } catch (e) {
            setLoading(false)
            toast.error(e.response && e.response.data ? t(e.response.data.message) : t(e.message), TOAST_OPTIONS);
        }
    }

    const deactivate = async (user_uuid) => {
        if (window.confirm(t('admin.confirm_deactivation'))) {
            try {
                const resp = await AdminService.deactivateUser(user_uuid);
                if (resp.status) {
                    toast.success(t('admin.account_deactivated'), TOAST_OPTIONS);
                    loadUsers();
                } else
                    toast.error(resp.message, TOAST_OPTIONS);
            } catch (e) {
                toast.error(e.response && e.response.data ? t(e.response.data.message) : t(e.message), TOAST_OPTIONS);
            }
        }
    }

    const reactivate = async (user_uuid) => {
        try {
            const resp = await AdminService.reactivateUser(user_uuid);
            if (resp.status) {
                toast.success(t('admin.account_reactivated'), TOAST_OPTIONS);
                loadUsers();
            } else
                toast.error(resp.message, TOAST_OPTIONS);
        } catch (e) {
            toast.error(e.response && e.response.data ? t(e.response.data.message) : t(e.message), TOAST_OPTIONS);
        }
    }

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }

    const getSortedAccounts = () => {
        if (!accounts || accounts.length === 0) return [];
        
        const sorted = [...accounts].sort((a, b) => {
            let aValue, bValue;
            
            switch(sortField) {
                case 'name':
                    aValue = `${a.firstname || ''} ${a.lastname || ''}`.trim().toLowerCase();
                    bValue = `${b.firstname || ''} ${b.lastname || ''}`.trim().toLowerCase();
                    break;
                case 'email':
                    aValue = (a.email_address || '').toLowerCase();
                    bValue = (b.email_address || '').toLowerCase();
                    break;
                case 'created_date':
                    aValue = new Date(a.created_date).getTime();
                    bValue = new Date(b.created_date).getTime();
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        
        return sorted;
    }

    const renderSortIcon = (field) => {
        if (sortField !== field) return ' ⇅';
        return sortDirection === 'asc' ? ' ↑' : ' ↓';
    }

    const exportToCSV = () => {
        if (!accounts || accounts.length === 0) {
            toast.warn('No data to export', TOAST_OPTIONS);
            return;
        }

        const sortedData = getSortedAccounts();
        
        // Define CSV headers
        const headers = pending 
            ? ['Email', 'Created Date', 'Pending Claims', 'Total Amount']
            : ['Name', 'Email', 'KYC Status', 'Created Date', 'Pending Claims', 'Total Amount', 'Public Address'];
        
        // Create CSV rows
        const rows = sortedData.map(account => {
            if (pending) {
                return [
                    account.email_address || '',
                    new Date(account.created_date).toLocaleDateString(),
                    account.token_claims?.to_claim?.length || 0,
                    account.token_claims?.total_to_claim || 0
                ];
            } else {
                return [
                    `${account.firstname || ''} ${account.lastname || ''}`.trim() || '-',
                    account.email_address || '',
                    account.kyc_status || 'No KYC',
                    new Date(account.created_date).toLocaleDateString(),
                    account.token_claims?.to_claim?.length || 0,
                    account.token_claims?.total_to_claim || 0,
                    account.public_address || ''
                ];
            }
        });
        
        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const fileName = `lifise_users_${pending ? 'pending' : deactivated ? 'deactivated' : 'active'}_${new Date().toISOString().split('T')[0]}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('CSV exported successfully', TOAST_OPTIONS);
    }

    useEffect(() => {
        // Prevent page caching
        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', handleBackButton);
        
        // Check authentication
        if (!TokenService.isUser()) {
            navigate('/admin/signin', { replace: true });
            return;
        }
        
        loadUsers();
        
        return () => {
            window.removeEventListener('popstate', handleBackButton);
        };
        // eslint-disable-next-line
    }, []);

    const handleBackButton = () => {
        if (!TokenService.isUser()) {
            navigate('/admin/signin', { replace: true });
        } else {
            window.history.pushState(null, null, window.location.pathname);
        }
    };

    return (
        <div className="dashboard">
            <Menu />
            <div className="right_board">
                <BoardHeader title={t('admin.lifise_users')} />
                <div className="content">
                    {/* <div className="search mb-20">
                        <input type="text" className="semi" placeholder="Search email or address" />
                        <Button title={"Search"} />
                    </div> */}
                    {displayForm ?
                        <React.Fragment>
                            <label>{t('admin.email_addresses')} <small>{t('admin.seperate_comma')} ","</small></label>
                            <div className="relative display-inline-block">
                                <textarea placeholder={t('admin.emails_link')} onChange={e => onChangeEmails(e.target.value)}></textarea>
                            </div>
                            <label>{t('admin.amount_send')} <small>{t('admin.optionnal')}</small></label>
                            <div className="relative display-inline-block">
                                <input type="number" className="semi" placeholder={t('admin.caaeuro_amount')} onChange={e => onChangeAmount(e.target.value)} />
                                <img src={LOGO_BLACK} className="caaeuro" alt="Logo CâaEuro" />
                            </div>
                            <Button title={t('admin.send')} loading={loading} click={sendInvits} />
                            <ThreeDots visible={loading} height="50" width="50" color="#1F90FA" radius="9" ariaLabel="three-dots-loading" />
                        </React.Fragment>
                        :
                        <div className="float-right">
                            <Button title={t('admin.send_invits')} click={() => setDisplayForm(true)} />
                        </div>
                    }
                    <div className='mt-30'>
                        <button 
                            onClick={exportToCSV}
                            className="btn_export"
                        >
                            Export to CSV
                        </button>
                    </div>
                    <div className="mt-10">
                        <div className="relative display-inline-block">
                            <label>
                                {t('admin.filter')} : <span className={deactivated ? "filter" : "filter heavy"} onClick={() => window.location.href = "/admin/users"}>{t('admin.activated')}</span> | <span className={deactivated ? "filter heavy" : "filter"} onClick={() => window.location.href = "/admin/users?deactivated=true"}>{t('admin.deactivated')}</span> | <span className={pending ? "filter heavy" : "filter"} onClick={() => window.location.href = "/admin/users?pending=true"}>{t('admin.pending_cap')}</span>
                            </label>
                        </div>
                    </div>
                    <ThreeDots visible={!accounts || (accounts.length === 0)} height="50" width="50" color="#1F90FA" radius="9" ariaLabel="three-dots-loading" />
                    
                    {accounts && accounts.length > 0 && (
                        <div className="users-table-container">
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th className="sortable" onClick={() => handleSort('name')}>
                                            Name{renderSortIcon('name')}
                                        </th>
                                        <th className="sortable" onClick={() => handleSort('email')}>
                                            Email{renderSortIcon('email')}
                                        </th>
                                        {!pending && <th>KYC Status</th>}
                                        <th className="sortable" onClick={() => handleSort('created_date')}>
                                            Created Date{renderSortIcon('created_date')}
                                        </th>
                                        {!pending && <th>Pending Claims</th>}
                                        <th className="center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getSortedAccounts().map((account, index) => (
                                        <tr key={account.user_uuid}>
                                            <td className="name-cell" data-label="Name">
                                                {pending ? '-' : `${account.firstname || '-'} ${account.lastname || '-'}`}
                                            </td>
                                            <td className="email-cell" data-label="Email">
                                                {account.email_address}
                                            </td>
                                            {!pending && (
                                                <td data-label="KYC Status">
                                                    {account.kyc_status ? (
                                                        account.kyc_status === "APPROVED" ? (
                                                            <span className="kyc-status-approved">✓ {account.kyc_status}</span>
                                                        ) : (
                                                            <span className="kyc-status-pending">{account.kyc_status}</span>
                                                        )
                                                    ) : (
                                                        <span className="kyc-status-none">⚠ {t('dashboard.no_kyc')}</span>
                                                    )}
                                                </td>
                                            )}
                                            <td data-label="Created Date">
                                                {new Date(account.created_date).toLocaleDateString()}
                                            </td>
                                            {!pending && (
                                                <td data-label="Pending Claims">
                                                    <div>
                                                        <strong>{account.token_claims?.to_claim?.length || 0}</strong> {t('admin.pending')}
                                                        <br />
                                                        <small className="claims-amount">{account.token_claims?.total_to_claim || 0} CaaEUR</small>
                                                    </div>
                                                </td>
                                            )}
                                            <td className="center" data-label="Actions">
                                                <div className="action-buttons">
                                                    {pending ? (
                                                        <span className="pending-icon">🕒</span>
                                                    ) : (
                                                        <>
                                                            <button
                                                                className="table-action-btn"
                                                                onClick={() => window.open(`/admin/user/${account.user_uuid}`)}
                                                                title="View details"
                                                            >
                                                                ➕
                                                            </button>
                                                            {deactivated ? (
                                                                account.public_address && (
                                                                    <button
                                                                        className="table-action-btn"
                                                                        onClick={() => reactivate(account.user_uuid)}
                                                                        title="Reactivate"
                                                                    >
                                                                        ✅
                                                                    </button>
                                                                )
                                                            ) : (
                                                                <button
                                                                    className="table-action-btn"
                                                                    onClick={() => deactivate(account.user_uuid)}
                                                                    title="Deactivate"
                                                                >
                                                                    🗑️
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <ToastContainer />
        </div>
    )
};

export default AdminUsers;